import { useUpdateServiceBooking, useBusinesses } from "@/hooks/api-hooks";
import { Button, Loader, Select } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { Business, Reservation } from "@turizi/shared";
import { SERVICE_CATEGORIES } from "@turizi/shared";
import { useState } from "react";
import { IconBubbleTextFilled, IconClearAll, IconClearFormatting, IconCross, IconTrash, IconVacuumCleaner, IconX } from "@tabler/icons-react";
import { TagIcon } from "@/lib/tag-utils";

interface Props {
    reservation: Reservation;
    size?: 'xs' | 'sm' | 'md' | 'lg';
    editable?: boolean;
}

export default function ReservationCoordinatorSelector({
    reservation,
    size = 'sm',
    editable = true,
}: Props) {
    const { t } = useTranslation();
    const { mutateAsync: updateBooking } = useUpdateServiceBooking();
    const { data: businessesResponse, isLoading: loadingBusinesses } = useBusinesses({
        category: SERVICE_CATEGORIES.coordinator
    });
    const businesses = businessesResponse?.data || [];
    const [saving, setSaving] = useState(false);

    const bookings = reservation?.service_bookings || [];
    const firstCoordinatorId = bookings[0]?.coordinator_id;
    const isCommon = bookings.length > 0 && bookings.every(b => b.coordinator_id === firstCoordinatorId);
    const currentValue = isCommon && firstCoordinatorId ? firstCoordinatorId.toString() : undefined;

    const handleSave = async (value: string | null) => {
        if (!editable) return;
        const coordinatorId = value ? Number(value) : null;
        if (bookings.length === 0) return;
        setSaving(true);
        try {
            await Promise.all(
                bookings
                    .filter((booking) => booking.id)
                    .map((booking) =>
                        updateBooking({
                            id: booking.id!,
                            coordinator_id: coordinatorId,
                        })
                    )
            );
        } catch (err) {
            console.error("Failed to update coordinators", err);
        } finally {
            setSaving(false);
        }
    };

    const hasAnyCoordinator = bookings.some(b => b.coordinator_id !== null && b.coordinator_id !== undefined);

    const loading = saving || loadingBusinesses;

    if (!editable) {
        return (
            <div className="font-bold text-xs flex items-center gap-1">
                <TagIcon name="coordinator" hideText size="xxs" noBackground />
                {businesses.find((b) => b.id === firstCoordinatorId)?.name || '-'}
            </div>
        );
    }

    return (
        <Select
            className="flex-1"
            variant="filled"
            searchable
            size={size}
            placeholder={t('select_coordinator')}
            value={currentValue}
            data={businesses.map((business: Business) => ({
                value: business.id.toString(),
                label: business.name
            }))}
            onChange={handleSave}
            disabled={loading || !editable}
            rightSectionPointerEvents={(hasAnyCoordinator && editable) ? "visiblePainted" : "none"}
            rightSection={
                loading
                    ?
                    <Loader size="xs" color="black" />
                    : (hasAnyCoordinator && editable) ? (
                        <Button
                            className="shrink-0"
                            size="xs"
                            color="gray"
                            variant="subtle"
                            onClick={() => handleSave(null)}
                            disabled={loading || !editable}
                        >
                            <IconTrash size={18} />
                        </Button>
                    ) : undefined
            }
            comboboxProps={{ keepMounted: false }}
        />
    );
}
